<template>
  <div
      class="slider-container"
      :class="{ 'flex-gap': !arrowsInternal }"
  >
    <div class="slider-wrapper">

      <button
          v-if="arrowsInternal && !loading && canScrollLeft"
          @click="scrollLeft"
          class="slider-btn btn-left btn-internal slider-arrow"
          :disabled="!canScrollLeft"
      >
        <div
            class="icon-wrapper"
            :class="[showArrowsBg ? 'arrows-internal bg-darker-75' : 'w-10-h-10']"
        >
          <IllinoisIcons icon="chevron-left" :width="20" :height="16" />
        </div>
      </button>

      <div
          ref="slider"
          class="slider-scroll"
          @scroll.passive="onScroll"
          :style="{
          paddingLeft: edgeOffset + 'px',
          paddingRight: edgeOffset + 'px',
          scrollPaddingLeft: edgeOffset + 'px',
          scrollPaddingRight: edgeOffset + 'px',
          gap: gutter + 'px'
        }"
      >
        <template v-if="!loading">
          <slot></slot>
        </template>
        <template v-else>
          <div
              v-for="idx in skeletonCount"
              :key="idx"
              class="skeleton shimmer"
              :style="{
              width: skeletonItemWidth + 'px',
              height: skeletonItemHeight + 'px'
            }"
          ></div>
        </template>
      </div>

      <button
          v-if="arrowsInternal && !loading && canScrollRight"
          @click="scrollRight"
          class="slider-btn btn-right btn-internal slider-arrow"
          :disabled="!canScrollRight"
      >
        <div
            class="icon-wrapper"
            :class="[showArrowsBg ? 'arrows-internal bg-darker-75' : 'w-10-h-10']"
        >
          <IllinoisIcons icon="chevron-right" :width="18" :height="18" />
        </div>
      </button>
    </div>

    <button
        v-if="!arrowsInternal && !loading && canScrollLeft"
        @click="scrollLeft"
        class="slider-btn btn-left btn-external-left slider-arrow bg-darker"
        :disabled="!canScrollLeft"
    >
      <div class="icon-wrapper w-10-h-10">
        <IllinoisIcons icon="chevron-left" :width="20" :height="16" />
      </div>
    </button>

    <button
        v-if="!arrowsInternal && !loading && canScrollRight"
        @click="scrollRight"
        class="slider-btn btn-right btn-external-right slider-arrow bg-darker"
        :disabled="!canScrollRight"
    >
      <div class="icon-wrapper w-10-h-10">
        <IllinoisIcons icon="chevron-right" :width="18" :height="18" />
      </div>
    </button>
  </div>
</template>

<script>
import IllinoisIcons from './icons.vue';

export default {
  name: 'IllinoisSlider',
  components: {
    IllinoisIcons
  },
  props: {
    arrowsInternal: { type: Boolean, default: true },
    scrollAmount: { type: Number, default: 300 },
    showArrowsBg: { type: Boolean, default: true },
    loading: { type: Boolean, default: false },
    skeletonCount: { type: Number, default: 4 },
    skeletonItemWidth: { type: Number, default: 340 },
    skeletonItemHeight: { type: Number, default: 160 },
    edgeOffset: { type: Number, default: 20 },
    gutter: { type: Number, default: 16 }
  },
  data() {
    return {
      canScrollLeft: false,
      canScrollRight: true
    };
  },
  mounted() {
    this.updateScrollState();
    this.$nextTick(() => {
      this.updateScrollState();
    });
  },
  methods: {
    onScroll() {
      if (this._rafId) return;
      this._rafId = requestAnimationFrame(() => {
        this._rafId = null;
        this.updateScrollState();
      });
    },
    scrollLeft() {
      const slider = this.$refs.slider;
      slider.scrollBy({ left: -this.scrollAmount, behavior: 'smooth' });
    },
    scrollRight() {
      const slider = this.$refs.slider;
      slider.scrollBy({ left: this.scrollAmount, behavior: 'smooth' });
    },
    updateScrollState() {
      const slider = this.$refs.slider;
      if (!slider) return;

      this.canScrollLeft = slider.scrollLeft > 0;
      this.canScrollRight = slider.scrollLeft < slider.scrollWidth - slider.clientWidth;
    }
  }
};
</script>

<style scoped>
/* Variabili CSS: Sostituisci i valori con i colori reali del tuo progetto */
.slider-container {
  --illinois-text: #ffffff;
  --illinois-text-muted: #a0aec0;
  --illinois-darker: #1a202c;
  --illinois-darker-75: rgba(26, 32, 44, 0.75);
  --illinois-white-20: rgba(255, 255, 255, 0.2);
  --illinois-white-40: rgba(255, 255, 255, 0.4);
}

/* Layout base */
.slider-container {
  position: relative;
  width: 100%;
}
.flex-gap {
  display: flex;
  align-items: center;
  gap: 16px; /* gap-4 in tailwind */
}
.slider-wrapper {
  position: relative;
  flex: 1;
  overflow: hidden;
}

/* Freccette (Bottoni) */
.slider-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  transition: all 0.2s ease-in-out;
  outline: none;
  position: absolute;
  top: 50%;
  z-index: 10;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
}
.slider-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn-left {
  left: 0;
}
.btn-right {
  right: 0;
}
.btn-internal {
  transform: translateY(-50%);
}
.btn-external-left {
  /* translate-x-8 tailwind = 32px */
  transform: translate(-32px, -50%);
}
.btn-external-right {
  transform: translate(32px, -50%);
}

/* Colori e Sfondi Freccette */
.bg-darker {
  background-color: var(--illinois-darker);
}
.bg-darker:hover {
  background-color: var(--illinois-darker);
  opacity: 1;
}
.bg-darker-75 {
  background-color: var(--illinois-darker-75);
}

/* Contenitori Icone */
.icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  color: var(--illinois-text-muted);
  transition: all 0.2s ease-in-out;
}
.slider-btn:not(:disabled):hover .icon-wrapper {
  color: var(--illinois-text);
}
.w-10-h-10 {
  width: 40px; /* w-10 tailwind */
  height: 40px; /* h-10 tailwind */
}
.arrows-internal {
  width: 30px;
  height: 50px;
  border-radius: 0;
}

/* Skeletons */
.skeleton {
  border-radius: 4px; /* rounded tailwind */
  background-color: var(--illinois-white-20);
  color: var(--illinois-white-40);
}

/* Animazione Shimmer nativa */
.shimmer {
  background: #f6f7f8;
  background: linear-gradient(to right, rgba(255,255,255,0.05) 8%, rgba(255,255,255,0.15) 18%, rgba(255,255,255,0.05) 33%);
  background-size: 800px 100%;
  animation: shimmerAnim 1.5s linear infinite forwards;
}

@keyframes shimmerAnim {
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}

/* Scroll / Slider core behavior */
.slider-scroll {
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  /* Scroll snap behavior */
  scroll-snap-type: x mandatory;
  -webkit-scroll-snap-type: x mandatory;
}
.slider-scroll::-webkit-scrollbar {
  display: none;
}
.slider-scroll {
  -ms-overflow-style: none;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}
.slider-scroll > * {
  scroll-snap-align: start;
  -webkit-scroll-snap-align: start;
  flex-shrink: 0;
}

/* Frecce hover */
.slider-arrow {
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
}
.slider-container:hover .slider-arrow {
  opacity: 1;
}

/* Animazione Scale dell'icona (SVG) */
.slider-arrow svg,
.slider-arrow div svg {
  transition: transform 0.2s ease !important;
  transform: scale(1) !important;
}
.slider-arrow:hover svg,
.slider-arrow:hover div svg {
  transform: scale(1.5) !important;
}
</style>